import { HttpInterceptorFn, HttpParams, HttpResponse } from '@angular/common/http';
import { of, delay } from 'rxjs';
import { environment } from '../../../environments/environment';
import { MOCK_RESPONSES, MockResponse } from './mock-data.registry';

// Internal types for mock data
interface MockAvailabilityDay {
  date: string;
  rooms: MockRoomData[];
}

interface MockRoomData {
  id: number;
  name: string;
  totalRooms: number;
  availableRooms: number;
  basePrice: number;
  discount?: number;
  minNights?: number;
  status: 'open' | 'closed' | 'n/a';
  availability?: { status: string; value?: number }[];
  yieldManagement?: { minRooms: number; discount: number };
}

/** Pattern data for a single day's availability */
interface DayPatternData {
  availableRooms: number;
  discount?: number;
  minNights?: number;
  status: 'open' | 'closed' | 'n/a';
  availability?: { status: string; value?: number }[];
  yieldManagement?: { minRooms: number; discount: number };
}

/** Entry in the base pattern array */
interface BasePatternEntry {
  day: number;
  data: DayPatternData;
}

/**
 * Mock API Interceptor
 *
 * Intercepts HTTP requests and returns mock responses when enabled.
 * Useful for unblocking frontend development when backend endpoints are not ready.
 *
 * Enable by setting `environment.api.enableMocks = true`
 */
export const mockInterceptor: HttpInterceptorFn = (req, next) => {
  // Only mock if enabled in environment
  if (!environment.api.enableMocks) {
    return next(req);
  }

  const mockConfig = findMockResponse(req.method, req.url);

  if (mockConfig) {
    let responseData = mockConfig.data;

    // Handle dynamic data generation for availability endpoints
    if (mockConfig.dynamic) {
      responseData = generateDynamicAvailabilityData(req.method, req.url, req.params);
    }

    const response = new HttpResponse({
      status: mockConfig.status || 200,
      statusText: 'OK',
      body: responseData,
    });

    // Apply delay if specified
    const delayMs = mockConfig.delay || 0;
    return of(response).pipe(delay(delayMs));
  }

  // No mock found, proceed with real request
  return next(req);
};

/**
 * Find a mock response configuration for the given request
 */
function findMockResponse(method: string, url: string): MockResponse | null {
  // Normalize the URL by removing the base API URL
  const apiUrl = environment.api.url;
  const normalizedUrl = url.startsWith(apiUrl)
    ? url.substring(apiUrl.length)
    : url;

  // Try exact match first
  const exactKey = `${method} ${normalizedUrl}`;
  if (MOCK_RESPONSES[exactKey]) {
    return MOCK_RESPONSES[exactKey];
  }

  // Try pattern matching (supports :param syntax)
  for (const [pattern, mockConfig] of Object.entries(MOCK_RESPONSES)) {
    if (pattern.startsWith(method)) {
      if (matchesPattern(method, normalizedUrl, pattern)) {
        return mockConfig;
      }
    }
  }

  return null;
}

/**
 * Check if a URL matches a pattern with :param placeholders
 * Example: "/api/v2/hotels/:hotelId/rooms" matches "/api/v2/hotels/123/rooms"
 */
function matchesPattern(method: string, url: string, pattern: string): boolean {
  const [patternMethod, patternPath] = pattern.split(' ');

  // Method must match
  if (patternMethod !== method) {
    return false;
  }

  // Strip query parameters from URL for pattern matching
  const urlWithoutQuery = url.split('?')[0];

  // Convert pattern to regex
  const regexPattern = patternPath
    .replace(/:[^/]+/g, '[^/]+') // Replace :param with regex
    .replace(/\//g, '\\/'); // Escape slashes

  const regex = new RegExp(`^${regexPattern}$`);
  return regex.test(urlWithoutQuery);
}

/**
 * Generate dynamic availability data for any date range
 */
function generateDynamicAvailabilityData(method: string, url: string, requestParams?: HttpParams): unknown {
  const apiUrl = environment.api.url;
  const normalizedUrl = url.startsWith(apiUrl) ? url.substring(apiUrl.length) : url;

  // Extract hotel ID from URL
  const hotelIdMatch = normalizedUrl.match(/\/v2\/hotels\/(\d+)\/availability/);
  const hotelId = hotelIdMatch ? parseInt(hotelIdMatch[1]) : 1;

  // Parse date range parameters from Angular HttpParams
  let startDate: string | null = null;
  let endDate: string | null = null;

  if (requestParams) {
    startDate = requestParams.get('start_date');
    endDate = requestParams.get('end_date');
  }

  // Fallback: try parsing from URL query string
  if (!startDate || !endDate) {
    const urlParams = new URLSearchParams(url.split('?')[1] || '');
    startDate = startDate || urlParams.get('start_date');
    endDate = endDate || urlParams.get('end_date');
  }

  // Check if this is a single date request (e.g., /v2/hotels/1/availability/2024-12-01)
  const singleDateMatch = normalizedUrl.match(/\/v2\/hotels\/\d+\/availability\/(\d{4}-\d{2}-\d{2})/);

  if (singleDateMatch) {
    // Single date request
    const date = singleDateMatch[1];
    return {
      rooms: generateRoomDataForDate(date, hotelId)
    };
  } else if (startDate && endDate) {
    // Date range request
    const rangeData = generateAvailabilityDataForRange(startDate, endDate, hotelId);
    return {
      data: rangeData
    };
  }

  // Fallback: generate data for current month if no parameters found
  const now = new Date();
  const firstDay = new Date(now.getFullYear(), now.getMonth(), 1);
  const lastDay = new Date(now.getFullYear(), now.getMonth() + 1, 0);

  const fallbackStartDate = formatDateKey(firstDay);
  const fallbackEndDate = formatDateKey(lastDay);

  const fallbackData = generateAvailabilityDataForRange(fallbackStartDate, fallbackEndDate, hotelId);

  return {
    data: fallbackData
  };
}

/**
 * Generate availability data for a date range.
 * Uses a repeating 30-day pattern that cycles for any date range.
 */
function generateAvailabilityDataForRange(
  startDateStr: string,
  endDateStr: string,
  _hotelId: number
): MockAvailabilityDay[] {
  const start = new Date(startDateStr);
  const end = new Date(endDateStr);
  const data: MockAvailabilityDay[] = [];
  const basePattern = getBaseAvailabilityPattern();

  const current = new Date(start);
  while (current <= end) {
    const dayOfMonth = current.getDate();
    const dateStr = formatDateKey(current);
    const dayData = getPatternDataForDay(dayOfMonth, basePattern);
    const mockRoom = createMockRoomFromPattern(dayData);

    data.push({
      date: dateStr,
      rooms: [mockRoom]
    });

    current.setDate(current.getDate() + 1);
  }

  return data;
}

/**
 * Get the pattern data for a specific day of the month.
 * Uses modulo to cycle through the 30-day pattern.
 * Day 31 uses the pattern from day 1.
 */
function getPatternDataForDay(dayOfMonth: number, basePattern: BasePatternEntry[]): DayPatternData {
  if (dayOfMonth === 31) {
    return basePattern[0].data;
  }
  const patternIndex = (dayOfMonth - 1) % basePattern.length;
  return basePattern[patternIndex].data;
}

/**
 * Create a mock room data object from pattern data.
 * Returns a placeholder room that will be replaced by real room data in the component.
 */
function createMockRoomFromPattern(dayData: DayPatternData): MockRoomData {
  return {
    id: 999, // Placeholder ID
    name: 'Pattern Room', // Placeholder name
    totalRooms: 4, // Default value, will be overridden by real room data
    basePrice: 150, // Default value, will be overridden by real room data
    availableRooms: dayData.availableRooms || 0,
    discount: dayData.discount,
    minNights: dayData.minNights,
    status: dayData.status,
    yieldManagement: dayData.yieldManagement,
    availability: dayData.availability
  };
}

/**
 * Returns the base 30-day availability pattern.
 * This pattern includes various scenarios: open, closed, n/a, yield management, etc.
 */
function getBaseAvailabilityPattern(): BasePatternEntry[] {
  return [
    ...getPatternDays1To10(),
    ...getPatternDays11To20(),
    ...getPatternDays21To30()
  ];
}

/**
 * Pattern data for days 1-10
 */
function getPatternDays1To10(): BasePatternEntry[] {
  return [
    createOpenDayPattern(1, 3, 30, 1, 30),
    createOpenDayPattern(2, 2, 30, 1, 20),
    createOpenDayPattern(3, 4, 50, 2, 50),
    createOpenDayPattern(4, 1, 10, 1, 10),
    createOpenDayPattern(5, 3, 30, 1, 30),
    createOpenDayPattern(6, 2, 10, 2, 20),
    createOpenDayPattern(7, 4, 50, 1, 40),
    createOpenDayPattern(8, 1, 10, 1, 15),
    createClosedDayPattern(9),
    createOpenDayPattern(10, 2, 30, 2, 25)
  ];
}

/**
 * Pattern data for days 11-20
 */
function getPatternDays11To20(): BasePatternEntry[] {
  return [
    createOpenDayPattern(11, 3, 30, 1, 35),
    createOpenDayPattern(12, 4, 50, 1, 50),
    createOpenDayPattern(13, 1, 10, 3, 10),
    createOpenDayPattern(14, 2, 30, 2, 20),
    createNotAvailableDayPattern(15),
    createOpenDayPattern(16, 3, 30, 1, 30),
    createOpenDayPattern(17, 4, 50, 1, 45),
    createOpenDayPattern(18, 2, 30, 2, 20),
    createOpenDayPattern(19, 1, 10, 1, 15),
    createOpenDayPattern(20, 3, 30, 1, 35)
  ];
}

/**
 * Pattern data for days 21-30
 */
function getPatternDays21To30(): BasePatternEntry[] {
  return [
    createOpenDayPattern(21, 4, 50, 2, 50),
    createClosedDayPattern(22),
    createOpenDayPattern(23, 2, 30, 1, 25),
    createYieldManagementDayPattern(24),
    createClosedDayPatternWithMinNights(25, 2),
    createMixedAvailabilityDayPattern(26),
    createMultiTierDayPattern(27, 4, 10, 2, 40, 25, 15),
    createTwoTierDayPattern(28, 1, 10, 1, 20, 5),
    createNotAvailableDayPattern(29),
    createThreeTierDayPattern(30, 5, 30, 1, 35, 20, 8)
  ];
}

/**
 * Create an open day pattern with single availability tier
 */
function createOpenDayPattern(
  day: number,
  availableRooms: number,
  discount: number,
  minNights: number,
  availabilityValue: number
): BasePatternEntry {
  return {
    day,
    data: {
      availableRooms,
      discount,
      minNights,
      status: 'open' as const,
      availability: [{ status: 'available' as const, value: availabilityValue }]
    }
  };
}

/**
 * Create a closed day pattern
 */
function createClosedDayPattern(day: number): BasePatternEntry {
  return {
    day,
    data: {
      availableRooms: 0,
      status: 'closed' as const,
      availability: [{ status: 'closed' as const }]
    }
  };
}

/**
 * Create a closed day pattern with minimum nights requirement
 */
function createClosedDayPatternWithMinNights(day: number, minNights: number): BasePatternEntry {
  return {
    day,
    data: {
      availableRooms: 0,
      minNights,
      status: 'closed' as const,
      availability: [{ status: 'closed' as const }]
    }
  };
}

/**
 * Create a not available (n/a) day pattern
 */
function createNotAvailableDayPattern(day: number): BasePatternEntry {
  return {
    day,
    data: {
      availableRooms: 0,
      status: 'n/a' as const,
      availability: [{ status: 'na' as const }]
    }
  };
}

/**
 * Create day 24's yield management pattern
 */
function createYieldManagementDayPattern(day: number): BasePatternEntry {
  return {
    day,
    data: {
      availableRooms: 2,
      discount: 50,
      minNights: 3,
      status: 'open' as const,
      yieldManagement: { minRooms: 2, discount: 10 },
      availability: [
        { status: 'available' as const, value: 50 },
        { status: 'discount' as const, value: 30 },
        { status: 'low' as const, value: 10 }
      ]
    }
  };
}

/**
 * Create day 26's mixed availability pattern (available + n/a)
 */
function createMixedAvailabilityDayPattern(day: number): BasePatternEntry {
  return {
    day,
    data: {
      availableRooms: 3,
      discount: 30,
      minNights: 1,
      status: 'open' as const,
      availability: [
        { status: 'available' as const, value: 30 },
        { status: 'na' as const }
      ]
    }
  };
}

/**
 * Create a multi-tier day pattern (available, discount, low)
 */
function createMultiTierDayPattern(
  day: number,
  availableRooms: number,
  discount: number,
  minNights: number,
  availableValue: number,
  discountValue: number,
  lowValue: number
): BasePatternEntry {
  return {
    day,
    data: {
      availableRooms,
      discount,
      minNights,
      status: 'open' as const,
      availability: [
        { status: 'available' as const, value: availableValue },
        { status: 'discount' as const, value: discountValue },
        { status: 'low' as const, value: lowValue }
      ]
    }
  };
}

/**
 * Create a two-tier day pattern (available, low)
 */
function createTwoTierDayPattern(
  day: number,
  availableRooms: number,
  discount: number,
  minNights: number,
  availableValue: number,
  lowValue: number
): BasePatternEntry {
  return {
    day,
    data: {
      availableRooms,
      discount,
      minNights,
      status: 'open' as const,
      availability: [
        { status: 'available' as const, value: availableValue },
        { status: 'low' as const, value: lowValue }
      ]
    }
  };
}

/**
 * Create a three-tier day pattern (available, discount, low)
 */
function createThreeTierDayPattern(
  day: number,
  availableRooms: number,
  discount: number,
  minNights: number,
  availableValue: number,
  discountValue: number,
  lowValue: number
): BasePatternEntry {
  return {
    day,
    data: {
      availableRooms,
      discount,
      minNights,
      status: 'open' as const,
      availability: [
        { status: 'available' as const, value: availableValue },
        { status: 'discount' as const, value: discountValue },
        { status: 'low' as const, value: lowValue }
      ]
    }
  };
}

/**
 * Generate room data for a specific date
 */
function generateRoomDataForDate(date: string, hotelId: number): MockRoomData[] {
  const dateObj = new Date(date);

  // Generate mock data for the entire month and extract the specific date
  const startOfMonth = new Date(dateObj.getFullYear(), dateObj.getMonth(), 1);
  const endOfMonth = new Date(dateObj.getFullYear(), dateObj.getMonth() + 1, 0);

  const monthData = generateAvailabilityDataForRange(
    formatDateKey(startOfMonth),
    formatDateKey(endOfMonth),
    hotelId
  );

  const dayData = monthData.find(d => d.date === date);
  return dayData ? dayData.rooms : [];
}

/**
 * Format date to YYYY-MM-DD
 */
function formatDateKey(date: Date): string {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}
